<template>
  <!-- CONTENIDO -->
  <div class="blogpost-page">
    <div class="container">
      <!-- Breadcrumbs -->
      <nav class="breadcrumb-nav" aria-label="breadcrumb" data-aos="fade-up" data-aos-duration="6000">
        <ol class="breadcrumb">
          <li class="breadcrumb-item"><router-link to="/">Inicio</router-link></li>
          <li class="breadcrumb-item"><router-link to="/blog">Blog</router-link></li>
          <li class="breadcrumb-item active" aria-current="page">
            {{ article?.title?.[selectedLang] || "Artículo" }}
          </li>
        </ol>
      </nav>

      <!-- Loader / error -->
      <div v-if="!article && !loading" class="empty-state" data-aos="fade-up" data-aos-duration="6000">
        <p>No se encontró el artículo.</p>
      </div>
      <div v-else-if="loading" class="loader">Cargando...</div>

      <div v-else data-aos="fade-left" data-aos-duration="6000">
        <div class="container">
          <div class="row">
            <div class="col-lg-2">
              <!-- Autor y fecha -->
              <div class="blog-author-row d-flex align-items-center mt-3 mb-1">
                <img src="https://cdn-icons-png.flaticon.com/512/149/149071.png" class="author-avatar" width="40" />
                <div class="author-info ms-2">
                  <div class="author-name">{{ article.author?.name }}</div>
                  <div class="author-date">
                    {{ formattedDate(article.date) }}
                  </div>
                </div>
              </div>
              <!-- Share -->
              <div class="blog-share my-2 d-flex flex-column">
                <span>Compartir: </span>
                <a :href="facebookShareUrl" target="_blank" rel="noopener" title="Compartir en Facebook">
                  <img src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/facebook.svg" class="share-icon" />
                </a>
                <a :href="linkedinShareUrl" target="_blank" rel="noopener" title="Compartir en LinkedIn">
                  <img src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/linkedin.svg" class="share-icon" />
                </a>
                <a :href="instagramUrl" target="_blank" rel="noopener" title="Ir a Instagram">
                  <img src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/instagram.svg" class="share-icon" />
                </a>
              </div>
            </div>

            <div class="col-lg-10">
              <!-- Hero con categoría, título e imagen -->
              <h1 class="blog-title">{{ article.title[selectedLang] }}</h1>
              <div class="blog-category">{{ article.category?.[selectedLang]?.[0] || '' }}</div>
              <img
                data-aos="fade-left" data-aos-duration="6000"
                :src="mainImage"
                :alt="article.title[selectedLang]"
                class="blog-hero-img"
              />

              <!-- Contenido -->
              <div class="blog-content">
                <template v-for="(block, index) in article.content[selectedLang]" :key="index">
                  <h2 v-if="block.type === 'heading'" class="content-heading" data-aos="fade-left" data-aos-duration="6000">{{ block.text?.[selectedLang] ?? block.text }}</h2>
                  <p v-else-if="block.type === 'paragraph'" class="content-paragraph" data-aos="fade-left" data-aos-duration="6000">{{ block.text?.[selectedLang] ?? block.text }}</p>
                  <ul v-else-if="block.type === 'list' && Array.isArray(block.text?.[selectedLang] ?? block.text)" data-aos="fade-left" data-aos-duration="6000">
                    <li v-for="(item, i) in (block.text?.[selectedLang] ?? block.text)" :key="i">{{ item }}</li>
                  </ul>
                  <p v-else-if="block.type === 'list' && !Array.isArray(block.text?.[selectedLang] ?? block.text)" class="content-list" data-aos="fade-left" data-aos-duration="6000">• {{ block.text?.[selectedLang] ?? block.text }}</p>
                  <img
                    data-aos="fade-left" data-aos-duration="6000"
                    v-else-if="block.type === 'image'"
                    :src="block.url"
                    :alt="block.alt?.[selectedLang] || ''"
                    class="content-image my-3"
                  />
                </template>
              </div>
            </div>
          </div>
        </div>

        <!-- Otros artículos -->
        <div class="container" data-aos="fade-up" data-aos-duration="1200">
          <div class="otros-articulos mt-5">
            <h3 class="otros-title">Otros artículos</h3>
            <div class="row">
              <template v-if="otherArticles.length">
                <div
                  v-for="item in otherArticles.slice(0, 3)"
                  :key="item.id"
                  class="curso-card col-12 col-md-6 col-lg-4 mb-4 d-flex"
                >
                  <div
                    class="otros-card h-100"
                    @click="goToArticle(item.slug)"
                    style="cursor:pointer;"
                  >
                    <img
                      :src="getFirstImage(item) || placeholder"
                      :alt="item.title[selectedLang]"
                      class="otros-img"
                    />
                    <div class="otros-meta d-flex align-items-center mt-2">
                      <img src="https://cdn-icons-png.flaticon.com/512/149/149071.png" class="author-avatar" width="22" />
                      <div class="ms-2">
                        <div class="author-name">{{ item.author?.name }}</div>
                        <div class="author-date">{{ formattedDate(item.date) }}</div>
                      </div>
                    </div>
                    <div class="otros-title-small fw-bold">{{ item.title[selectedLang] }}</div>
                    <div class="otros-summary">
                      {{ getSummary(item.content[selectedLang]) }}
                    </div>
                  </div>
                </div>
              </template>
              <div v-else class="empty-otros col-12">No hay artículos relacionados.</div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>

  <!-- FOOTER fuera del wrapper para evitar conflictos -->
  <SiteFooter :selectedLang="selectedLang" />
</template>

<script>
import { ref as vRef } from 'vue'
import { useHead } from '@vueuse/head'
import { database, ref, get } from '@/firebase.js'
import SiteFooter from "@/components/SiteFooter.vue";
import AOS from 'aos'
import 'aos/dist/aos.css'

const BASE_URL = 'https://www.frenkel.com.mx'

export default {
  name: "BlogPost",
  components: { SiteFooter },
  props: {
    selectedLang: { type: String, default: "es" }
  },
  setup(props) {
    const seoTitle = vRef('Blog | Frenkel Engineering')
    const seoDescription = vRef('Artículos y noticias sobre Industria 4.0, automatización industrial y transformación digital por los expertos de Frenkel Engineering.')
    const seoImage = vRef(`${BASE_URL}/og-image.png`)
    const seoUrl = vRef(`${BASE_URL}/blog`)

    // Función para actualizar SEO dinámicamente cuando se carga el artículo
    const updateSEO = (article, slug) => {
      if (article) {
        const title = article.title?.[props.selectedLang] || article.title?.es || 'Blog'
        const excerpt = article.excerpt?.[props.selectedLang] || article.excerpt?.es || 'Artículo de Industria 4.0'
        const image = article.headerImage || `${BASE_URL}/og-image.png`
        const url = slug ? `${BASE_URL}/blog/${slug}` : `${BASE_URL}/blog`

        seoTitle.value = `${title} | Blog Frenkel`
        seoDescription.value = excerpt
        seoImage.value = image
        seoUrl.value = url
      }
    }

    useHead({
      title: seoTitle,
      meta: [
        { name: 'description', content: seoDescription },
        { name: 'robots', content: 'index, follow' },
        { property: 'og:type', content: 'article' },
        { property: 'og:title', content: seoTitle },
        { property: 'og:description', content: seoDescription },
        { property: 'og:image', content: seoImage },
        { property: 'og:url', content: seoUrl },
        { property: 'og:site_name', content: 'Frenkel Engineering' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: seoTitle },
        { name: 'twitter:description', content: seoDescription },
        { name: 'twitter:image', content: seoImage },
      ],
      link: [{ rel: 'canonical', href: seoUrl }],
    })

    return { seoTitle, seoDescription, seoImage, seoUrl, updateSEO }
  },
  data() {
    return {
      article: null,
      loading: true,
      allArticles: [], // Todos los artículos de Firebase
      otherArticles: [],
      placeholder: "https://www.edsrobotics.com/wp-content/uploads/2020/05/industria-4.0-900x480.jpg",
      instagramUrl: "https://www.instagram.com/frenkel.mx"
    };
  },
  computed: {
    mainImage() {
      if (!this.article) return this.placeholder;
      const img = this.article.headerImage
      return img || this.placeholder;
    },
    facebookShareUrl() {
      if (!this.article) return "#";
      const url = window.location.href;
      return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    },
    linkedinShareUrl() {
      if (!this.article) return "#";
      const url = window.location.href;
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    }
  },
  watch: {
    '$route.params.slug': {
      handler() { this.loadArticle(); },
      immediate: true
    },
    selectedLang() { this.loadArticle(); }
  },
  methods: {
    async fetchAllArticles() {
      try {
        // Referencia al nodo 'blogArticles' en Realtime Database
        const articlesRef = ref(database, 'blogArticles')
        
        // Obtener los datos
        const snapshot = await get(articlesRef)
        
        if (snapshot.exists()) {
          const data = snapshot.val()
          
          // Convertir el objeto a array y agregar el ID
          this.allArticles = Object.keys(data).map(key => ({
            id: key,
            ...data[key]
          }))
          
          console.log('Artículos cargados:', this.allArticles.length)
        } else {
          console.log('No se encontraron artículos')
          this.allArticles = []
        }
      } catch (error) {
        console.error('Error al cargar artículos:', error)
        this.allArticles = []
      }
    },
    
    async loadArticle() {
      this.loading = true;

      // Si no hemos cargado los artículos, hacerlo primero
      if (this.allArticles.length === 0) {
        await this.fetchAllArticles();
      }

      const { slug } = this.$route.params;
      const found = this.allArticles.find(art => art.slug === slug);
      this.article = found || null;
      this.loading = false;

      if (found) {
        this.updateSEO(found, slug);
        this.loadOtherArticles();
        this.scrollToTop();
      }
    },
    
    loadOtherArticles() {
      if (!this.article) return;
      const cats = this.article.category?.[this.selectedLang] || [];
      
      // Buscar artículos relacionados por categoría
      let related = this.allArticles.filter(
        art =>
          art.id !== this.article.id &&
          art.category?.[this.selectedLang]?.some(c => cats.includes(c))
      );
      
      // Si no hay suficientes, agregar artículos aleatorios
      if (related.length < 3) {
        const pool = this.allArticles.filter(art => art.id !== this.article.id);
        while (related.length < 3 && pool.length) {
          const idx = Math.floor(Math.random() * pool.length);
          const next = pool.splice(idx, 1)[0];
          if (next && !related.find(r => r.id === next.id)) related.push(next);
        }
      }
      
      this.otherArticles = related.slice(0, 3);
    },
    
    getFirstImage(article) {
      const blocks = article.content?.[this.selectedLang] || [];
      const img = blocks.find(b => b.type === "image");
      return img?.url || this.placeholder;
    },
    
    formattedDate(date) {
      const d = new Date(date);
      return d.toLocaleDateString(this.selectedLang, { year: 'numeric', month: 'short', day: '2-digit' });
    },
    
    getSummary(blocks) {
      if (!blocks) return "";
      const firstParagraph = blocks.find(b => b.type === "paragraph");
      if (!firstParagraph) return "";
      const text = firstParagraph.text?.[this.selectedLang] ?? firstParagraph.text ?? "";
      return text.length > 240 ? text.slice(0, 240) + "..." : text;
    },
    
    goToArticle(slug) {
      if (slug !== this.article.slug) {
        this.$router.push({ name: "BlogPost", params: { slug } });
      } else {
        this.scrollToTop();
      }
    },
    
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  },
  
  async mounted() {
    await this.loadArticle();
    AOS.init({ duration: 800, once: true });
    setTimeout(() => AOS.refresh(), 300);
  }
};
</script>

<style scoped>
.blogpost-page {
  background: #f6fafb;
  /* Importante: que el wrapper no "empuje" al footer */
  padding-bottom: 32px;
  overflow-x: hidden;
}

.breadcrumb-nav { margin-top: 24px; margin-bottom: 10px; }

.blog-title {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 2.2rem; font-weight: 700;
  text-align: justify;
  margin: 12px 0 0;
  transition: color .3s ease;
  position: relative;
}
.blog-category {
  font-family: 'IBM Plex Mono', monospace;
  text-align: justify; color: #757575; margin-bottom: 10px;
}

.blog-hero-img {
  display: block; max-width: 100%; width: 900px; max-height: 480px;
  margin: 0 auto 14px; object-fit: cover;
  border: 2px solid #222; background: #fff;
  transition: transform .4s, box-shadow .4s, filter .4s, opacity .4s;
}
.blog-hero-img:hover { transform: scale(1.05); box-shadow: 0 10px 20px rgba(0,0,0,.3); cursor: pointer; }

.blog-author-row { gap: 12px; }
.author-avatar { border-radius: 100%; border: 1px solid #eee; transition: transform .3s, box-shadow .3s; }
.author-avatar:hover { transform: scale(1.05); box-shadow: 0 10px 20px rgba(0,0,0,.3); cursor: pointer; }
.author-name { font-family: 'IBM Plex Mono', monospace; font-size: 1rem; font-weight: 700; color: #222; }
.author-date { font-size: .92rem; color: #888; font-family: 'IBM Plex Mono', monospace; }

.blog-share { display: flex; align-items: center; gap: 13px; font-size: 1.07rem; margin-left: 2px; }
.blog-share a:hover { transform: scale(1.05); border-radius: 50%; box-shadow: 0 10px 20px rgba(0,0,0,.3); }
.share-icon { width: 21px; height: 21px; filter: grayscale(.5); transition: filter .2s; }
.share-icon:hover { filter: none; }

.blog-content { margin: 24px auto 0; max-width: 900px; font-family: 'IBM Plex Mono', monospace; }
.content-heading {
  font-size: 1.35rem; font-weight: 700; margin: 24px 0 12px;
  position: relative; display: inline-block; transition: color .3s ease;
}
.content-heading::after, .otros-title::after {
  content: ''; position: absolute; left: 0; bottom: 0; height: 2px; width: 0;
  background-color: currentColor; transition: width .3s ease;
}
.content-heading:hover::after, .otros-title:hover::after { width: 100%; }

.content-paragraph { font-size: 1.02rem; margin-bottom: 10px; }
.content-list { margin-bottom: 4px; }

.content-image {
  max-width: 100%; display: block; margin: 18px auto;
  border: 1px solid #aaa; border-radius: 6px;
  transition: transform .3s, box-shadow .3s;
}
.content-image:hover { transform: scale(1.05); box-shadow: 0 10px 20px rgba(0,0,0,.3); cursor: pointer; }

.otros-articulos { margin-top: 42px; }
.curso-card { transition: transform .3s, box-shadow .3s, border-color .2s; }
.curso-card:hover { transform: scale(1.05); box-shadow: 0 2px 13px rgba(100,100,120,.08); border-color: #2c74b8; }
.otros-title { font-family: 'Caveat-Bold', cursive; font-size: 2rem; margin-bottom: 16px; position: relative; display: inline-block; }

.otros-card {
  background: #fff; border: 1px solid #e1e1e1; border-radius: 13px;
  padding: 13px 13px 9px; height: 100%;
  display: flex; flex-direction: column; justify-content: flex-start;
  transition: box-shadow .2s;
}
.otros-card:hover { box-shadow: 0 3px 18px 2px #d0d0d0; }
.otros-img { width: 100%; height: 120px; object-fit: cover; border-radius: 7px; background: #f6fafb; border: 1px solid #bbb; }
.otros-meta { font-size: .9rem; color: #333; margin-top: 6px; }
.otros-title-small { font-size: 1.09rem; margin: 6px 0 2px; }
.otros-summary { font-size: .97rem; color: #666; margin-bottom: 3px; }

.empty-state, .loader, .empty-otros { text-align: justify; color: #444; font-size: 1.14rem; margin: 36px 0; }

@media (max-width: 991.98px) {
  .blog-hero-img, .blog-content { width: 100%; max-width: 100%; }
}
</style>
